/**
 * Small runtime guards for the API's free-form JSON columns (`Project.concept`,
 * `Job.progress`, `Asset.meta`, a generation's `response`…), typed `unknown` in the
 * schema. Reading them through these, instead of casting, turns a backend shape change
 * into a missing value rather than a crash or a wrong one.
 */

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** The value as an object, or `{}`. */
export function record(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {};
}

export function str(value: unknown, key: string): string | undefined {
  const field = record(value)[key];
  return typeof field === "string" ? field : undefined;
}

export function num(value: unknown, key: string): number | undefined {
  const field = record(value)[key];
  return typeof field === "number" && Number.isFinite(field) ? field : undefined;
}

export function bool(value: unknown, key: string): boolean | undefined {
  const field = record(value)[key];
  return typeof field === "boolean" ? field : undefined;
}

export function strArray(value: unknown, key: string): string[] | undefined {
  const field = record(value)[key];
  return Array.isArray(field) ? field.filter((x): x is string => typeof x === "string") : undefined;
}

export type LyricsCandidate = { title: string; lyrics: string };

/** The candidates of a lyrics generation's `response`. */
export function lyricsCandidates(response: unknown): LyricsCandidate[] {
  const list = record(response).candidates;
  if (!Array.isArray(list)) {
    return [];
  }
  return list.flatMap((c) => {
    const title = str(c, "title");
    const lyrics = str(c, "lyrics");
    return title !== undefined && lyrics !== undefined ? [{ title, lyrics }] : [];
  });
}

/** A job's stored error (`{"code", "message"}`), or `null`. */
export function jobError(error: unknown): { code?: string; message?: string } | null {
  if (!isRecord(error)) {
    return null;
  }
  return { code: str(error, "code"), message: str(error, "message") };
}

/** A render job's live progress, as the builder reports it. */
export function jobProgress(progress: unknown): {
  phase?: string;
  fps?: number;
  eta_ms?: number;
  log_tail?: string;
} {
  return {
    phase: str(progress, "phase"),
    fps: num(progress, "fps"),
    eta_ms: num(progress, "eta_ms"),
    log_tail: str(progress, "log_tail"),
  };
}
